import type {
  KorintModulesProductsConfig,
  FormSections,
  FormSectionsWithShortcuts,
  ConditionalVisibility,
  CustomFunnelConfig,
  FieldShortcut,
} from 'korint-sdk/react';
import { CustomFunnelAuth } from 'korint-sdk/react';
import { tutassurHeader } from '../layout/header';
import { tutassurFooter } from '../layout/footer';
import {
  AlertTriangle,
  BadgeCheck,
  BookOpenCheck,
  Box,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Car,
  Euro,
  FilePen,
  FileText,
  FileUp,
  FolderOpen,
  Ghost,
  Globe,
  Hash,
  Home,
  Key,
  Layers,
  LayoutGrid,
  Mail,
  MapPin,
  Maximize2,
  PartyPopper,
  Percent,
  Phone,
  Scale,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Store,
  User,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import { ContactKey } from '@folio/utils/customerDisplayName';
import {
  policyControllerConfirmPolicy,
  policyControllerGeneratePolicySignatureRequest,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import { DocumentsUploadPolicyModule, InvoicesScheduleModule } from '@folio/modules';
import { createAnonymousGeneratedDocumentsStep } from '../../shared/anonymousGeneratedDocumentsStep';
import z, { type ZodTypeAny } from 'zod';

// ============================================================================
// Form Sections
// ============================================================================
const assetFunnelFields: FieldShortcut[] = [
  { name: 'assetAddress', icon: MapPin, type: 'address' },
  { name: 'assetAddressComplement', icon: MapPin },
  { name: 'assetPostalCode', colSpan: 4 as const, icon: Hash },
  { name: 'assetCity', type: 'city', colSpan: 8 as const, icon: Building2 },
  {
    name: 'assetLotType',
    icons: {
      APARTMENT: Building2,
      HOUSE: Home,
      PARKING: Car,
      ISOLATED_GARAGE: Car,
      BOX: Box,
      REAL_ESTATE_COMMERCIAL: Store,
    },
  },
  {
    name: 'assetFloor',
    icon: Layers,
    type: 'number' as const,
    integer: true,
    display: 'cards' as const,
    displayMin: 0,
    displayMax: 9,
    labels: { 0: 'RDC' },
  },
  {
    name: 'assetMainRoomsNumber',
    icon: LayoutGrid,
    type: 'number' as const,
    integer: true,
    display: 'cards' as const,
    displayMin: 1,
    displayMax: 10,
  },
  { name: 'assetSurface', icon: Maximize2 },
  {
    name: 'assetIsOccupiedByTenant',
    icon: Key,
    icons: {
      OCCUPIED: Key,
      UNOCCUPIED_OR_UNDER_RENOVATION: Ghost,
    },
    showWhen: [
      { fieldName: 'assetLotType', condition: 'in' as const, values: ['APARTMENT', 'HOUSE'] },
    ],
  },
  {
    name: 'assetLocationType',
    icon: CalendarCheck,
    icons: {
      LONG_TERM: CalendarCheck,
      SHORT_TERM: CalendarClock,
      SHARED_WITH_SEPARATE_LEASES: Users,
    },
    showWhen: [
      { fieldName: 'assetLotType', condition: 'in' as const, values: ['APARTMENT', 'HOUSE'] },
      { fieldName: 'assetIsOccupiedByTenant', condition: 'equals' as const, value: 'OCCUPIED' },
    ],
  },
];

const killingQuestionField = (
  name: string,
  blockingValue: 'true' | 'false' = 'true',
  showWhen?: ConditionalVisibility[],
) => ({
  name,
  ...(showWhen && { showWhen }),
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(
      z.literal(blockingValue === 'true' ? 'false' : 'true', {
        errorMap: () => ({
          message: 'Il est impossible de souscrire une police dans ce cas',
        }),
      }),
    ),
});

const showWhenLocalCommercial = [
  {
    apiProperty: 'asset.customFields.assetLotType',
    condition: 'equals' as const,
    value: 'REAL_ESTATE_COMMERCIAL',
  },
] as const satisfies ConditionalVisibility[];

const policyFunnelSections: FormSectionsWithShortcuts = [
  {
    title: "Déclarations de l'assuré",
    fields: [
      {
        ...killingQuestionField('isCustomerRealEstateAgentOrPromoter'),
        icon: ShieldAlert,
        display: 'toggle' as const,
        blockingValue: 'true',
      },
      { name: 'wasPreviouslyInsured', icon: ShieldCheck, display: 'toggle' as const },
      {
        ...killingQuestionField('hasAtLeastOneClaimInTheLast36Months'),
        icon: AlertTriangle,
        display: 'toggle' as const,
        blockingValue: 'true',
      },
      {
        ...killingQuestionField('hadPreviousContractCancelledByInsurer'),
        icon: XCircle,
        display: 'toggle' as const,
        blockingValue: 'true',
      },
      {
        ...killingQuestionField('doesNotHostExcludedActivities', 'false', showWhenLocalCommercial),
        icon: Store,
        display: 'toggle' as const,
        blockingValue: 'false',
      },
    ],
  },
] satisfies FormSectionsWithShortcuts;

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: true,
  },
] as const satisfies ConditionalVisibility[];

const showWhenLegalPerson = [
  { fieldName: 'isCustomerLegalPerson', condition: 'equals', value: 'true' },
] as const satisfies ConditionalVisibility[];

const customerFunnelSections = [
  {
    title: 'Informations du propriétaire',
    fields: [
      { name: 'isCustomerLegalPerson', icon: Building2, display: 'toggle' as const },
      {
        ...killingQuestionField('isCustomerSci', 'false', showWhenLegalPerson),
        icon: Scale,
        display: 'toggle' as const,
        blockingValue: 'false',
      },
    ],
  },
  {
    title: "Informations de l'entreprise",
    fields: [
      { name: 'isCompanyBeingCreated', type: 'checkbox' },
      {
        name: 'companySiret',
        type: 'company',
        isCustomField: true,
        displayCompanyInformation: true,
        icon: Search,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                  city: apiData?.customFields?.companyHeadquarterCity,
                  address: apiData?.customFields?.companyHeadquarterAddress,
                },
                nafCode: apiData?.customFields?.companyNafCode,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        showWhen: [{ fieldName: 'isCompanyBeingCreated', condition: 'equals', value: false }],
      },
      {
        name: 'companyName',
        showWhen: showWhenCompanyBeingCreated,
        required: true,
        DEPRECATED_placeholder: 'Acme SAS',
        icon: Building2,
      },
      {
        name: 'companyLegalStatus1',
        showWhen: showWhenCompanyBeingCreated,
        required: true,
        DEPRECATED_placeholder: 'SAS, SARL, SA...',
        icon: FileText,
      },
      {
        name: 'companyCreationDate',
        showWhen: showWhenCompanyBeingCreated,
        required: true,
        colSpan: 6 as const,
        icon: CalendarDays,
      },
      {
        name: 'companyHeadquarterAddress',
        type: 'address',
        showWhen: showWhenCompanyBeingCreated,
        required: true,
        DEPRECATED_placeholder: '12 rue de la Paix',
        icon: MapPin,
      },
      {
        name: 'companyHeadquarterPostcode',
        type: 'postcode',
        showWhen: showWhenCompanyBeingCreated,
        required: true,
        DEPRECATED_placeholder: '75001',
        colSpan: 4 as const,
        icon: Hash,
      },
      {
        name: 'companyHeadquarterCity',
        type: 'city',
        showWhen: showWhenCompanyBeingCreated,
        required: true,
        DEPRECATED_placeholder: 'Paris',
        colSpan: 8 as const,
        icon: Building2,
      },
      {
        name: 'companyHeadquarterCountry',
        type: 'country',
        showWhen: showWhenCompanyBeingCreated,
        required: true,
        DEPRECATED_placeholder: 'France',
        colSpan: 6 as const,
        icon: Globe,
      },
      {
        name: 'companyNafCode',
        showWhen: showWhenCompanyBeingCreated,
        DEPRECATED_placeholder: '6201Z',
        colSpan: 6 as const,
        icon: Hash,
      },
    ],
    showWhen: [
      { fieldName: 'isCustomerLegalPerson', condition: 'equals', value: 'true' },
      { fieldName: 'isCustomerSci', condition: 'equals', value: 'true' },
    ],
  },
  {
    title: 'Informations du signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      { name: 'contactFirstName', colSpan: 6 as const, icon: User },
      { name: 'contactLastName', colSpan: 6 as const, icon: User },
      { name: 'contactEmail', colSpan: 6 as const, icon: Mail },
      { name: 'contactPhoneNumber', colSpan: 6 as const, icon: Phone },
      { name: 'contactBirthDate', colSpan: 4 as const, icon: CalendarDays },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        colSpan: 4 as const,
        icon: MapPin,
      },
      {
        name: 'contactBirthCountry',
        type: 'country',
        addressGroup: 'contactBirth',
        colSpan: 4 as const,
        icon: Globe,
      },
      { name: 'contactAddress', icon: MapPin, type: 'address' },
      { name: 'contactPostCode', type: 'postcode', colSpan: 4 as const, icon: Hash },
      { name: 'contactCity', type: 'city', colSpan: 8 as const, icon: Building2 },
    ],
  },
] satisfies FormSectionsWithShortcuts;

const quoteAssetSections = [
  {
    title: "Date de prise d'effet de l'assurance",
    fields: [
      {
        name: 'startedAt',
        required: true,
        type: 'datetime',
        min: 'now',
        icon: CalendarDays,
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const quotePolicySections: FormSectionsWithShortcuts = [
  {
    title: 'Franchise',
    fields: [
      {
        name: 'chosenExcess',
        required: true,
        type: 'select',
        options: [
          { value: '170', label: '170 €' },
          { value: '290', label: '290 €' },
          { value: '400', label: '400 €' },
        ],
        icons: { '170': Euro, '290': Euro, '400': Euro },
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.chosenExcess?.toString() ?? '290',
        formDataAccessor: (formData: Record<string, unknown>) => Number(formData.chosenExcess),
      },
    ],
  },
] satisfies FormSectionsWithShortcuts;

const additionalQuoteComponentsSections = [
  {
    title: 'Frais et commissions grossiste',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'wholesaleAdditionalCommission',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        max: 100,
        colSpan: 6 as const,
        icon: Percent,
      },
      {
        name: 'wholesaleSubscriptionFee',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        colSpan: 6 as const,
        icon: Euro,
        apiDataAccessor: (apiData: { customFields: Record<string, number> }) =>
          (apiData.customFields.wholesaleSubscriptionFee ?? 0) / 100,
        formDataAccessor: (formData: Record<string, unknown>) =>
          Number(formData.wholesaleSubscriptionFee ?? 0) * 100,
      },
    ],
  },
  {
    title: 'Frais et commissions distributeur',
    forTypes: ['RETAIL'],
    fields: [
      {
        name: 'retailFee',
        type: 'number',
        required: false,
        min: 0,
        max: 100,
        colSpan: 6 as const,
        icon: Percent,
        apiDataAccessor: (apiData: {
          brokerageFees?: { brokerageFirmId: string; fee: number }[];
          brokerageFirm?: string;
        }) =>
          apiData.brokerageFees?.find(
            ({ brokerageFirmId }) => brokerageFirmId === apiData.brokerageFirm,
          )?.fee ?? 0,
        formDataAccessor: (formData: Record<string, unknown>) => ({
          fees: [
            {
              brokerageFirmId: formData.brokerageFirm,
              fee: Number(formData.retailFee ?? 0),
            },
          ],
        }),
      },
      {
        name: 'retailSubscriptionFee',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        colSpan: 6 as const,
        icon: Euro,
        apiDataAccessor: (apiData: { customFields: Record<string, number> }) =>
          (apiData.customFields.retailSubscriptionFee ?? 0) / 100,
        formDataAccessor: (formData: Record<string, unknown>) =>
          Number(formData.retailSubscriptionFee ?? 0) * 100,
      },
      {
        name: 'retailRenewalFee',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        icon: CalendarDays,
        apiDataAccessor: (apiData: { customFields: Record<string, number> }) =>
          (apiData.customFields.retailRenewalFee ?? 0) / 100,
        formDataAccessor: (formData: Record<string, unknown>) =>
          Number(formData.retailRenewalFee ?? 0) * 100,
      },
    ],
  },
] as const satisfies Array<FormSections[number] & { forTypes?: string[] }>;

// ============================================================================
// Anonymous Funnel Steps
// ============================================================================
const anonymousDocumentsStep = {
  path: 'documents',
  type: 'module' as const,
  icon: FileUp,
  module: (
    <DocumentsUploadPolicyModule
      displayContext="policyholder"
      documentsLists={[
        { category: 'id-card', required: true },
        { category: 'iban', required: true },
        { category: 'rental-lease' },
        { category: 'kbis' },
        { category: 'autre-document' },
      ]}
    />
  ),
};

const anonymousGeneratedDocumentsStep = createAnonymousGeneratedDocumentsStep({
  categories: ['PNO_QUOTE', 'PNO_TERMS_AND_CONDITIONS', 'PNO_IPID'],
  requiredDocuments: [
    <span>Votre pièce d'identité</span>,
    <span>Votre RIB (IBAN)</span>,
    <span>Votre bail de location (optionnel)</span>,
    <span>Votre extrait KBIS (optionnel)</span>,
    <span>Tout autre document utile (optionnel)</span>,
  ],
  label: 'Documents',
  icon: FolderOpen,
});

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

// ============================================================================
// Anonymous Funnel Base Definitions
// ============================================================================

type AnonymousFunnelBase = Omit<
  CustomFunnelConfig,
  'name' | 'basePath' | 'afterEndPath' | 'layout' | 'customLayout'
>;

const tutassurCustomLayout = {
  header: tutassurHeader,
  footer: tutassurFooter,
};

const anonymousDiscoveryBase: AnonymousFunnelBase = {
  auth: CustomFunnelAuth.ANONYMOUS,
  steps: [
    {
      path: 'asset',
      type: 'assetFunnel',
      icon: Home,
      fields: assetFunnelFields,
      minAssets: 1,
      maxAssets: 1,
    },
    {
      path: 'customer',
      type: 'customerFunnel',
      icon: User,
      sections: customerFunnelSections,
      triggerEvent: 'TUTASSUR_ANONYMOUS_PRICING_COMPLETED',
      maxCustomers: 1,
      minCustomers: 1,
    },
    {
      path: 'policy',
      type: 'policyFunnel',
      icon: BookOpenCheck,
      sections: policyFunnelSections,
    },
    {
      path: 'billing',
      type: 'billingSelectionFunnel',
      icon: CalendarClock,
      frequencyIcons: BILLING_FREQUENCY_ICONS,
    },
    {
      path: 'quote',
      type: 'quoteFunnel',
      icon: Shield,
      assetSections: quoteAssetSections,
      policySections: quotePolicySections,
      additionalQuoteComponentsSections,
    },
    {
      path: 'invoicing-schedule',
      type: 'module',
      icon: CalendarDays,
      module: <InvoicesScheduleModule />,
    },
    anonymousGeneratedDocumentsStep,
  ],
};

const anonymousSubscriptionBase: AnonymousFunnelBase = {
  auth: CustomFunnelAuth.ANONYMOUS,
  steps: [
    anonymousDocumentsStep,
    {
      path: 'payment-methods',
      type: 'paymentMethodFunnel',
      icon: Wallet,
    },
    {
      path: 'end',
      type: 'funnelEnd' as const,
      id: 'anonymous-subscription-end',
      icon: PartyPopper,
      callback: {
        callToActionCallback: async ({
          policyId,
          branchId,
        }: {
          policyId: string;
          branchId: string;
        }) => {
          await policyControllerPushPolicyEvent(
            policyId,
            { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
            { branchId },
          );
          await policyControllerConfirmPolicy(policyId, { branchId });
          for (let attempt = 0; attempt < 100; attempt++) {
            try {
              await policyControllerGeneratePolicySignatureRequest(
                policyId,
                { deliveryMode: 'EMAIL', isOtp: false, phoneNumber: '' },
                { branchId },
              );
              break;
            } catch (error: unknown) {
              if ((error as any)?.errorResponse?.status === 425 && attempt < 99) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
              }
              throw error;
            }
          }
        },
      },
      redirectTo: ({ policyId, branchId }: { policyId: string; branchId: string }) =>
        `/auth?redirect=${encodeURIComponent(`/policyholder/${policyId}/${branchId}`)}`,
      additionalComponents: {
        isQuoteTierModuleVisible: false,
        finishWithoutSendingButton: {
          enabled: false,
        },
      },
    },
  ],
};

// ============================================================================
// Product Configuration
// ============================================================================
export const tutassurMilaProprietaireNonOccupantConfig = {
  display: {
    category: 'habitation',
    icon: Home,
    badges: ['online'],
  },
  productCustomPath: '/pno-delegated',

  // ============================================================================
  // Customer Information Display
  // ============================================================================
  customerInformations: {
    fieldsConfig: [
      'contactCivility',
      { key: 'contactFirstName', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', contactKey: ContactKey.LAST_NAME },
      'contactEmail',
      'contactBirthDate',
      'contactBirthCity',
      'contactBirthCountry',
      'contactPhoneNumber',
      'contactAddress',
      'contactPostCode',
      'contactCity',
      {
        key: 'isCustomerLegalPerson',
        type: 'boolean',
      },
      { key: 'companyName', type: 'text', hideIfEmpty: true },
      { key: 'companySiret', type: 'siret', hideIfEmpty: true },
      { key: 'companyNafCode', type: 'text', hideIfEmpty: true },
      { key: 'companyLegalStatus1', type: 'text', hideIfEmpty: true },
      { key: 'companyCreationDate', type: 'date', hideIfEmpty: true },
      {
        key: 'companyHeadquarterAddress',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'companyHeadquarterPostcode',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'companyHeadquarterCity',
        type: 'text',
        hideIfEmpty: true,
      },
    ],
  },

  // ============================================================================
  // Asset Information Display
  // ============================================================================
  assetInformations: {
    fieldsConfig: [
      'assetAddress',
      'assetAddressComplement',
      'assetPostalCode',
      'assetCity',
      'assetLotType',
      'assetFloor',
      'assetMainRoomsNumber',
      'assetSurface',
      'assetIsOccupiedByTenant',
      'assetLocationType',
    ],
  },

  // ============================================================================
  // Quote & Premium Display
  // ============================================================================
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'INSURANCE_PREMIUM', variant: 'bold' }] },
      {
        lines: [{ key: 'WHOLESALE_ADDITIONAL_COMMISSION', variant: 'bold' }],
      },
      {
        lines: [{ key: 'KORINT_MANAGEMENT_FEE', variant: 'bold' }],
      },
      {
        lines: [{ key: 'RETAIL_COMMISSION', variant: 'bold' }],
      },
      {
        variant: 'callout',
        lines: [
          { key: 'ATTACK_TAX', variant: 'dimmed' },
          { key: 'SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'WHOLESALE_SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'RENEWAL_FEE', variant: 'dimmed' },
          { key: 'RETAIL_SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'RETAIL_RENEWAL_FEE', variant: 'dimmed' },
        ],
      },
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/pno-delegated/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/pno-delegated/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },

  // ============================================================================
  // Discovery Funnel (New Business)
  // ============================================================================
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Home,
        fields: assetFunnelFields,
        minAssets: 1,
        maxAssets: 1,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'policy',
        type: 'policyFunnel',
        icon: BookOpenCheck,
        sections: policyFunnelSections,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        assetSections: quoteAssetSections,
        policySections: quotePolicySections,
        additionalQuoteComponentsSections,
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },

  // ============================================================================
  // Anonymous Funnels (Policyholder Self-Service)
  // ============================================================================
  customFunnels: [
    {
      ...anonymousDiscoveryBase,
      name: 'anonymous-discovery',
      basePath: '/anonymous/:brokerageFirmId/pno-delegated/:customerId/:policyId/:branchId?',
      afterEndPath:
        '/anonymous/:brokerageFirmId/pno-delegated-souscription/:customerId/:policyId/:branchId?/documents',
      layout: 'custom' as const,
      customLayout: tutassurCustomLayout,
    },
    {
      ...anonymousDiscoveryBase,
      name: 'anonymous-discovery-embedded',
      basePath:
        '/anonymous-embedded/:brokerageFirmId/pno-delegated/:customerId/:policyId/:branchId?',
      afterEndPath:
        '/anonymous-embedded/:brokerageFirmId/pno-delegated-souscription/:customerId/:policyId/:branchId?/documents',
      layout: 'embedded' as const,
    },
    {
      ...anonymousSubscriptionBase,
      name: 'anonymous-subscription',
      basePath:
        '/anonymous/:brokerageFirmId/pno-delegated-souscription/:customerId/:policyId/:branchId?',
      layout: 'custom' as const,
      customLayout: tutassurCustomLayout,
    },
    {
      ...anonymousSubscriptionBase,
      name: 'anonymous-subscription-embedded',
      basePath:
        '/anonymous-embedded/:brokerageFirmId/pno-delegated-souscription/:customerId/:policyId/:branchId?',
      layout: 'embedded' as const,
    },
  ],

  // ============================================================================
  // MTA Funnel (Mid-Term Adjustment)
  // ============================================================================
  mtaFunnelSteps: {
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Home,
        maxAssets: 1,
        fields: assetFunnelFields,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'policy',
        type: 'policyFunnel',
        icon: BookOpenCheck,
        sections: policyFunnelSections,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        assetSections: quoteAssetSections,
        policySections: quotePolicySections,
        additionalQuoteComponentsSections,
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },

      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
        callback: {},
      },
    ],
  },

  // ============================================================================
  // Policy Dashboard Configuration
  // ============================================================================
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: false,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
    enablePolicyComparison: false,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: ['POLICYHOLDER'],
  },

  // ============================================================================
  // Finalize New Business
  // ============================================================================
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-import',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },

  // ============================================================================
  // Policy Information Card
  // ============================================================================
  policyInformationCard: {
    title: "Déclarations de l'assuré",
    fieldsConfig: [
      {
        key: 'isCustomerRealEstateAgentOrPromoter',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
      {
        key: 'wasPreviouslyInsured',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
      {
        key: 'hasAtLeastOneClaimInTheLast36Months',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
      {
        key: 'hadPreviousContractCancelledByInsurer',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
      {
        key: 'doesNotHostExcludedActivities',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
      {
        key: 'chosenExcess',
        type: 'text',
        formatValue: value => {
          if (value === undefined) return undefined;
          return `${value} €`;
        },
      },
    ],
  },
} satisfies KorintModulesProductsConfig;
